import { useForm } from 'react-hook-form';
import AddressAutocomplete from 'shared/components/forms/AddressAutocomplete';
import { usePlaceAddressField } from 'shared/hooks/usePlaceAddressField';
import { Field, INPUT, INPUT_ERR } from './form-utils';

export interface PickupData {
  pickupAddress: string;
  pickupLat: number;
  pickupLng: number;
  senderFirstName: string;
  senderLastName: string;
  senderPhone: string;
  senderEmail: string;
}

interface Props {
  defaultValues?: Partial<PickupData>;
  onNext: (data: PickupData) => void;
}

export default function StepPickup({ defaultValues, onNext }: Props) {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<PickupData>({ defaultValues, mode: 'onSubmit', reValidateMode: 'onSubmit' });

  const pickupField = usePlaceAddressField(register, setValue, getValues, trigger, {
    address: 'pickupAddress',
    lat: 'pickupLat',
    lng: 'pickupLng',
  });
  const { ref: pickupRef, onBlur: pickupBlur, name: pickupName } = pickupField.registration;

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <div className="fade-up bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-4">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span
              className="material-symbols-outlined text-primary text-[20px]"
              style={{ fontVariationSettings: '"FILL" 1' }}
            >
              location_on
            </span>
          </div>
          <h3 className="font-black text-base text-slate-800">Pick-Up Details</h3>
        </div>

        <div className="flex flex-col gap-4">
          <Field label="Pick-up address" error={errors.pickupAddress?.message} hint="Start typing and choose from suggestions">
            <AddressAutocomplete
              key={`pickup-${defaultValues?.pickupAddress ?? ''}-${defaultValues?.pickupLat ?? ''}`}
              name={pickupName}
              ref={pickupRef}
              defaultValue={defaultValues?.pickupAddress}
              onBlur={pickupBlur}
              onChange={pickupField.handleAddressChange}
              onPlaceSelect={pickupField.handlePlaceSelect}
              onManualEdit={pickupField.handleManualEdit}
              placeholder="123 Main St, Lagos"
              className={`${INPUT} ${errors.pickupAddress ? INPUT_ERR : ''}`}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First name" error={errors.senderFirstName?.message}>
              <input
                {...register('senderFirstName', { required: 'Required' })}
                placeholder="Ade"
                className={`${INPUT} ${errors.senderFirstName ? INPUT_ERR : ''}`}
              />
            </Field>
            <Field label="Last name" error={errors.senderLastName?.message}>
              <input
                {...register('senderLastName', { required: 'Required' })}
                placeholder="Johnson"
                className={`${INPUT} ${errors.senderLastName ? INPUT_ERR : ''}`}
              />
            </Field>
            <Field label="Phone" error={errors.senderPhone?.message}>
              <input
                {...register('senderPhone', {
                  required: 'Required',
                  pattern: { value: /^[0-9+\s()-]+$/, message: 'Invalid phone number' },
                })}
                type="tel"
                placeholder="08012345678"
                className={`${INPUT} ${errors.senderPhone ? INPUT_ERR : ''}`}
              />
            </Field>
            <Field label="Email" error={errors.senderEmail?.message}>
              <input
                {...register('senderEmail', {
                  required: 'Required',
                  pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' },
                })}
                type="email"
                placeholder="ade@email.com"
                className={`${INPUT} ${errors.senderEmail ? INPUT_ERR : ''}`}
              />
            </Field>
          </div>
        </div>
      </div>

      <button
        type="submit"
        className="w-full bg-primary text-white font-extrabold px-8 py-4 rounded-2xl text-base hover:shadow-xl hover:shadow-primary/25 hover:scale-[1.02] transition-all active:scale-95 flex items-center justify-center gap-2 relative overflow-hidden group mt-2"
      >
        <span className="absolute inset-0 bg-primary-deep scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 rounded-2xl" />
        <span className="relative z-10">Continue to Delivery Details</span>
        <span className="material-symbols-outlined relative z-10 text-[20px]">arrow_forward</span>
      </button>
    </form>
  );
}
